import { type ReactNode, useCallback, useEffect, useState } from 'react';
import {
  type MktCampaign,
  type MktLeadDetail,
  type MktMessage,
  type MktThread,
  createDraft,
  disqualifyLead,
  draftNow,
  enrollLeads,
  getLead,
  getThreadMessages,
  listThreads,
  regenerateMessage,
  sendMessage,
  updateCampaign,
  updateMessage,
} from '../lib/internal.js';
import { Badge, type Notify, type Tone, btn, btnGhost, input } from './ui.js';

const STATUS_TONE: Record<string, Tone> = {
  queued: 'neutral',
  drafted: 'amber',
  revised: 'indigo',
  sent: 'sky',
  replied: 'emerald',
  bounced: 'rose',
  unsubscribed: 'rose',
  skipped: 'neutral',
  active: 'emerald',
  paused: 'amber',
  draft: 'neutral',
};

/** Small inline spinner using the current text color. */
function Spin() {
  return (
    <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70" />
  );
}

/**
 * The working surface for one campaign — STRICTLY INTERNAL (admin-only): the
 * enroll/generate toolbar plus the threaded inbox (AI drafts → edit / regenerate
 * / send, and inbound replies). Shared between the inline Marketing tab and the
 * full-screen campaign manager so both behave identically. `tall` gives the
 * full-screen layout taller scroll regions; `showStatusToggle` hides the
 * pause/resume button when the host already provides one.
 */
export function CampaignInbox({
  campaign,
  notify,
  onChanged,
  showActions = true,
  showStatusToggle = true,
  tall = false,
}: {
  campaign: MktCampaign;
  notify: Notify;
  onChanged?: () => void;
  /** Show the enroll / generate (+ optional auto-draft) action toolbar. */
  showActions?: boolean;
  showStatusToggle?: boolean;
  tall?: boolean;
}) {
  const [threads, setThreads] = useState<MktThread[]>([]);
  const [openLead, setOpenLead] = useState<string | null>(null);
  const [messages, setMessages] = useState<MktMessage[]>([]);
  const [lead, setLead] = useState<MktLeadDetail | null>(null);
  const [action, setAction] = useState<string | null>(null);
  // Filter the inbox by lead/thread status (all | queued | drafted | sent | replied).
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const busy = action !== null;

  const loadThreads = useCallback(async () => {
    try {
      setThreads(await listThreads(campaign.id));
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to load threads');
    }
  }, [campaign.id, notify]);

  // Reset + reload the inbox whenever the campaign changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on campaign switch
  useEffect(() => {
    setOpenLead(null);
    setMessages([]);
    setLead(null);
    loadThreads();
  }, [campaign.id]);

  const openThread = async (leadId: string) => {
    setOpenLead(leadId);
    setLead(null);
    try {
      setMessages(await getThreadMessages(campaign.id, leadId));
      // Prospect facts for the detail card above the thread (best-effort).
      getLead(leadId)
        .then(setLead)
        .catch(() => {});
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to load thread');
    }
  };

  const wrap = async (id: string, fn: () => Promise<void>, ok?: string) => {
    setAction(id);
    try {
      await fn();
      if (ok) notify(ok, 'success');
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setAction(null);
    }
  };

  const enroll = () =>
    wrap('enroll', async () => {
      const { enrolled } = await enrollLeads(campaign.id, 50);
      notify(`Enrolled ${enrolled} lead(s)`, 'success');
      await loadThreads();
      onChanged?.();
    });

  const generate = () =>
    wrap('generate', async () => {
      const res = await draftNow(campaign.id);
      await loadThreads();
      onChanged?.();
      if (res.drafted > 0) {
        const failed = (res.failed ?? 0) > 0 ? `, ${res.failed} failed` : '';
        notify(
          `Drafted ${res.drafted} email(s)${failed}. Review & send below; run again for the next batch.`,
          'success',
        );
      } else if ((res.queued ?? 0) === 0) {
        notify('Nothing to draft — enroll leads first (or today’s batch is full).', 'success');
      } else {
        notify(`Couldn’t draft ${res.failed ?? 0} lead(s) — the AI returned no usable output.`);
      }
    });

  const toggleStatus = () =>
    wrap('status', async () => {
      const next = campaign.status === 'active' ? 'paused' : 'active';
      await updateCampaign(campaign.id, { status: next });
      onChanged?.();
      notify(
        next === 'active'
          ? 'Auto-drafting ON — the cron will draft this campaign’s daily batch'
          : 'Auto-drafting paused',
        'success',
      );
    });

  const send = (messageId: string) =>
    wrap(
      'send',
      async () => {
        await sendMessage(messageId);
        if (openLead) setMessages(await getThreadMessages(campaign.id, openLead));
        await loadThreads();
        onChanged?.();
      },
      'Sent',
    );

  // "Not a fit": suppress the lead, delete its unsent draft, and skip its
  // enrollments so it's never queued again. Closes the thread once done.
  const disqualify = (leadId: string) =>
    wrap(
      'disqualify',
      async () => {
        await disqualifyLead(leadId);
        setOpenLead(null);
        setMessages([]);
        setLead(null);
        await loadThreads();
        onChanged?.();
      },
      'Lead marked ineligible — draft removed, won’t be queued again',
    );

  const label = (id: string, idle: ReactNode, busyText: string): ReactNode =>
    action === id ? (
      <span className="inline-flex items-center gap-1.5">
        <Spin /> {busyText}
      </span>
    ) : (
      idle
    );

  const listH = tall ? 'max-h-[calc(100vh-23rem)]' : 'max-h-[28rem]';
  const filtered = threads.filter((t) => statusFilter === 'all' || t.status === statusFilter);
  const openThreadEmail = threads.find((t) => t.leadId === openLead)?.email ?? null;

  return (
    <div>
      {showActions && (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <button type="button" className={btnGhost} disabled={busy} onClick={enroll}>
              {label('enroll', '1. Enroll 50 leads', 'Enrolling…')}
            </button>
            <button type="button" className={btn} disabled={busy} onClick={generate}>
              {label('generate', '2. Generate drafts (AI)', 'Generating drafts…')}
            </button>
            {showStatusToggle && (
              <button
                type="button"
                className={btnGhost}
                disabled={busy}
                onClick={toggleStatus}
                title="Optional: let the 15-minute cron draft this campaign's daily batch automatically"
              >
                {label(
                  'status',
                  campaign.status === 'active' ? 'Pause auto-drafting' : 'Start auto-drafting',
                  'Updating…',
                )}
              </button>
            )}
            <span className="text-xs text-neutral-500">
              Sends from {campaign.fromEmail} · Reply-To {campaign.replyTo} · BCC {campaign.bcc}
            </span>
          </div>
          {action === 'generate' && (
            <p className="mb-3 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
              <Spin /> Generating drafts with AI — one call per lead, this can take a few seconds.
              Drafts appear in the inbox as they're written.
            </p>
          )}
        </>
      )}
      <div
        className={`grid gap-4 ${tall ? 'lg:grid-cols-[300px_1fr]' : 'md:grid-cols-[300px_1fr]'}`}
      >
        <div className="rounded-lg border border-neutral-200">
          <div className="flex items-center justify-between gap-2 border-b border-neutral-100 px-3 py-2">
            <span className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Inbox ({filtered.length}
              {statusFilter !== 'all' ? ` of ${threads.length}` : ''})
            </span>
            <select
              className="rounded-md border border-neutral-200 bg-white px-1.5 py-0.5 text-xs text-neutral-600"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              title="Filter the inbox by status"
            >
              <option value="all">All</option>
              <option value="queued">Queued</option>
              <option value="drafted">Drafted</option>
              <option value="sent">Sent</option>
              <option value="replied">Replied</option>
              <option value="bounced">Bounced</option>
              <option value="unsubscribed">Unsubscribed</option>
              <option value="skipped">Ineligible</option>
            </select>
          </div>
          <ul className={`${listH} divide-y divide-neutral-100 overflow-y-auto`}>
            {filtered.map((t) => (
              <li key={t.leadId}>
                <button
                  type="button"
                  onClick={() => openThread(t.leadId)}
                  aria-current={openLead === t.leadId}
                  className={`w-full border-l-2 px-3 py-2 text-left text-sm transition ${openLead === t.leadId ? 'border-emerald-500 bg-emerald-50' : 'border-transparent hover:bg-neutral-50'}`}
                >
                  <div className="flex items-center justify-between gap-1.5">
                    <span className="truncate font-medium">{t.company ?? t.email}</span>
                    <span className="flex shrink-0 items-center gap-1">
                      {t.lastMessage?.status === 'revised' && <Badge tone="indigo">revised</Badge>}
                      <Badge tone={STATUS_TONE[t.status] ?? 'neutral'}>{t.status}</Badge>
                    </span>
                  </div>
                  <div className="truncate text-xs text-neutral-500">
                    {t.lastMessage
                      ? `${t.lastMessage.direction === 'inbound' ? '↩ ' : ''}${t.lastMessage.snippet}`
                      : 'no messages'}
                  </div>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-4 text-sm text-neutral-400">
                {threads.length === 0
                  ? 'Enroll leads, then generate drafts (or the cron AI-drafts here).'
                  : 'No threads match this status.'}
              </li>
            )}
          </ul>
        </div>

        <div className="rounded-lg border border-neutral-200 p-3">
          {!openLead ? (
            <p className="text-sm text-neutral-400">Select a thread to view + send.</p>
          ) : (
            <div className="space-y-3">
              <ProspectCard lead={lead} fallbackEmail={openThreadEmail} />
              <Thread
                campaignId={campaign.id}
                leadId={openLead}
                messages={messages}
                busy={busy}
                sending={action === 'send'}
                disqualifying={action === 'disqualify'}
                onSend={send}
                onDisqualify={disqualify}
                notify={notify}
                tall={tall}
                onRefresh={() => openThread(openLead)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Compact prospect-details card shown above the thread. */
function ProspectCard({
  lead,
  fallbackEmail,
}: {
  lead: MktLeadDetail | null;
  fallbackEmail: string | null;
}) {
  if (!lead) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-400">
        Loading prospect… {fallbackEmail}
      </div>
    );
  }
  const l = lead.lead;
  const name = [l.firstName, l.lastName].filter(Boolean).join(' ') || '—';
  const location = [l.city, l.state].filter(Boolean).join(', ');
  const fact = (label: string, value: ReactNode) =>
    value ? (
      <div>
        <div className="text-[10px] uppercase tracking-wide text-neutral-400">{label}</div>
        <div className="text-neutral-700">{value}</div>
      </div>
    ) : null;
  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50/70 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="truncate font-semibold text-neutral-900">{l.companyName ?? name}</h4>
            <Badge tone={tierTone(l.tier)}>T{l.tier}</Badge>
            {lead.suppressed && <Badge tone="rose">do-not-contact</Badge>}
          </div>
          <div className="text-sm text-neutral-600">
            {name}
            {l.title ? ` · ${l.title}` : ''}
          </div>
        </div>
        <div className="shrink-0 text-right text-xs text-neutral-500">
          <div>
            <b className="text-neutral-700">{lead.sentCount}</b> sent
          </div>
          <div>
            <b className="text-neutral-700">{lead.replyCount}</b> repl
            {lead.replyCount === 1 ? 'y' : 'ies'}
          </div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
        {fact('Email', l.email ? <span className="break-all">{l.email}</span> : null)}
        {fact('Segment', l.segment)}
        {fact('Location', location)}
        {fact('ICP score', l.icpScore != null ? String(l.icpScore) : null)}
        {fact(
          'Website',
          l.websiteCheckedUrl ? (
            <a
              href={l.websiteCheckedUrl}
              target="_blank"
              rel="noreferrer"
              className="text-emerald-700 hover:underline"
            >
              visit ↗
            </a>
          ) : l.domain ? (
            l.domain
          ) : null,
        )}
        {fact('Employees', l.employees != null ? String(l.employees) : null)}
      </div>
    </div>
  );
}

/** ICP tier → badge tone (0 best). */
function tierTone(t: number): Tone {
  return t === 0 ? 'emerald' : t === 1 ? 'sky' : t === 2 ? 'amber' : 'neutral';
}

function Thread({
  campaignId,
  leadId,
  messages,
  busy,
  sending,
  disqualifying,
  onSend,
  onDisqualify,
  notify,
  onRefresh,
  tall = false,
}: {
  campaignId: string;
  leadId: string;
  messages: MktMessage[];
  busy: boolean;
  sending: boolean;
  disqualifying: boolean;
  onSend: (id: string) => void;
  onDisqualify: (leadId: string) => void;
  notify: Notify;
  onRefresh: () => void;
  tall?: boolean;
}) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  // Inline edit of an AI draft before sending: which message + working copy.
  const [editId, setEditId] = useState<string | null>(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  // Which draft is being re-rolled by the AI (drives that row's spinner).
  const [regenId, setRegenId] = useState<string | null>(null);
  const regen = async (id: string) => {
    try {
      setRegenId(id);
      await regenerateMessage(id);
      onRefresh();
      notify('Draft regenerated', 'success');
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to regenerate draft');
    } finally {
      setRegenId(null);
    }
  };
  const addDraft = async () => {
    try {
      if (!subject.trim() || !body.trim()) throw new Error('Subject and body required');
      await createDraft(campaignId, leadId, { subject: subject.trim(), body: body.trim() });
      setSubject('');
      setBody('');
      onRefresh();
      notify('Draft saved', 'success');
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to save draft');
    }
  };
  const startEdit = (m: MktMessage) => {
    setEditId(m.id);
    setEditSubject(m.subject ?? '');
    setEditBody(m.body);
  };
  const cancelEdit = () => setEditId(null);
  const saveEdit = async (id: string) => {
    try {
      if (!editBody.trim()) throw new Error('Body required');
      setSavingEdit(true);
      await updateMessage(id, { subject: editSubject.trim(), body: editBody.trim() });
      setEditId(null);
      onRefresh();
      notify('Draft updated', 'success');
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to update draft');
    } finally {
      setSavingEdit(false);
    }
  };
  return (
    <div className="space-y-3">
      <ul
        className={`${tall ? 'max-h-[calc(100vh-24rem)] min-h-[24rem]' : 'max-h-[20rem]'} space-y-2 overflow-y-auto`}
      >
        {messages.map((m) => {
          const isDraft =
            m.direction === 'outbound' && (m.status === 'draft' || m.status === 'revised');
          const editing = editId === m.id;
          return (
            <li
              key={m.id}
              className={`rounded-lg border p-2.5 text-sm ${
                m.direction === 'inbound'
                  ? 'border-emerald-200 bg-emerald-50/40'
                  : m.status === 'revised'
                    ? 'border-indigo-300 bg-indigo-50/50'
                    : 'border-neutral-200'
              }`}
            >
              <div className="mb-1 flex items-center justify-between text-xs text-neutral-500">
                <span className="flex items-center gap-1.5">
                  {m.direction === 'inbound'
                    ? '↩ Reply from prospect'
                    : m.status === 'revised'
                      ? '✎ Revised draft'
                      : m.aiGenerated
                        ? '🤖 AI draft'
                        : 'Outbound'}
                  {m.direction === 'outbound' && (
                    <Badge tone={STATUS_TONE[m.status] ?? 'neutral'}>{m.status}</Badge>
                  )}
                </span>
                {isDraft && !editing && (
                  <span className="flex items-center gap-1.5">
                    <button
                      type="button"
                      className={`${btnGhost} text-rose-600 hover:bg-rose-50`}
                      disabled={busy || regenId === m.id}
                      onClick={() => {
                        if (
                          window.confirm(
                            'Mark this lead ineligible? This deletes the draft, adds them to the do-not-contact list, and stops them being queued again.',
                          )
                        )
                          onDisqualify(leadId);
                      }}
                      title="Not a fit: delete this draft and never contact this lead again"
                    >
                      {disqualifying ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Spin /> Removing…
                        </span>
                      ) : (
                        '🚫 Not a fit'
                      )}
                    </button>
                    <button
                      type="button"
                      className={btnGhost}
                      disabled={busy || regenId === m.id}
                      onClick={() => regen(m.id)}
                      title="Discard this draft and have the AI write a new one"
                    >
                      {regenId === m.id ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Spin /> Regenerating…
                        </span>
                      ) : (
                        '↻ Regenerate'
                      )}
                    </button>
                    <button
                      type="button"
                      className={btnGhost}
                      disabled={busy || regenId === m.id}
                      onClick={() => startEdit(m)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={btn}
                      disabled={busy || regenId === m.id}
                      onClick={() => onSend(m.id)}
                    >
                      {sending ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Spin /> Sending…
                        </span>
                      ) : (
                        'Send'
                      )}
                    </button>
                  </span>
                )}
              </div>
              {editing ? (
                <div className="mt-1 space-y-1">
                  <input
                    className={input}
                    placeholder="Subject"
                    value={editSubject}
                    onChange={(e) => setEditSubject(e.target.value)}
                  />
                  <textarea
                    className={`${input} h-32`}
                    placeholder="Message body"
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                  />
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      className={btn}
                      disabled={savingEdit}
                      onClick={() => saveEdit(m.id)}
                    >
                      {savingEdit ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Spin /> Saving…
                        </span>
                      ) : (
                        'Save changes'
                      )}
                    </button>
                    <button
                      type="button"
                      className={btnGhost}
                      disabled={savingEdit}
                      onClick={cancelEdit}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {m.subject && <div className="font-medium">{m.subject}</div>}
                  <div className="whitespace-pre-wrap text-neutral-700">{m.body}</div>
                </>
              )}
            </li>
          );
        })}
        {messages.length === 0 && <li className="text-sm text-neutral-400">No messages yet.</li>}
      </ul>
      <div className="border-t border-neutral-100 pt-2">
        <input
          className={input}
          placeholder="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <textarea
          className={`${input} mt-1 h-20`}
          placeholder="Write a draft…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <button type="button" className={`${btnGhost} mt-1`} onClick={addDraft}>
          Save draft
        </button>
      </div>
    </div>
  );
}
